/**
 * Platform-agnostic GTD API Client
 * 
 * This client can be used in any JavaScript/TypeScript environment:
 * - Next.js
 * - Vite
 * - CLI tools
 * - Node.js applications
 * - React Native
 * 
 * Features type-safe API contracts and standardized error handling.
 */

import { HttpClient } from './http-client';
import {
  ListResponse,
  ListQueryParams,
  GTDInboxItemDTO,
  GTDProjectDTO,
  GTDNextActionDTO,
  CaptureInboxItemRequest,
  RefineInboxItemRequest,
  ClarifyInboxItemRequest,
  CreateProjectRequest,
  CreateNextActionRequest,
  AssignNextActionRequest,
  InboxQueryFilters,
  ProjectQueryFilters,
  NextActionQueryFilters,
  ProcessInboxItemRequest,
  ProcessInboxResponse,
  WeeklyReviewResponse,
} from '../contracts';

import {
  BackendInboxListResponse,
  BackendProjectsListResponse,
  BackendCreateInboxItemRequest,
  BackendCreateInboxItemResponse,
  BackendInboxListResponseSchema,
  BackendProjectsListResponseSchema,
  isBackendSuccessResponse,
  BackendApiError,
  ApiValidationError,
} from '../contracts/api/backend-response-types';

import {
  classifyError,
  withRetry,
  defaultErrorLogger,
  ErrorLogger,
} from '../contracts/api/error-handling';

export interface GTDClientConfig {
  enableValidation?: boolean;
  enableRetry?: boolean;
  errorLogger?: ErrorLogger;
}

export class GTDClient {
  private readonly config: GTDClientConfig;

  constructor(
    private http: HttpClient,
    private basePath = '/api/productivity/gtd',
    config: GTDClientConfig = {}
  ) {
    this.config = {
      enableValidation: true,
      enableRetry: true,
      errorLogger: defaultErrorLogger,
      ...config,
    };
  }

  private async executeRequest<T>(operation: () => Promise<T>): Promise<T> {
    try {
      if (this.config.enableRetry) {
        return await withRetry(operation);
      }
      return await operation();
    } catch (error) {
      const standardError = classifyError(error);
      this.config.errorLogger?.logError(standardError);
      throw standardError;
    }
  }

  private validateResponse<T>(data: unknown, schema: any, operationName: string): T {
    if (!this.config.enableValidation) {
      return data as T;
    }

    const result = schema.safeParse(data);
    if (!result.success) {
      throw new ApiValidationError(
        `Invalid response format for ${operationName}`,
        result.error.issues
      );
    }
    return result.data;
  }

  // Inbox Operations
  async getInboxItems(
    filters?: InboxQueryFilters & ListQueryParams
  ): Promise<ListResponse<GTDInboxItemDTO>> {
    return this.executeRequest(async () => {
      const queryParams = filters ? new URLSearchParams(
        Object.entries(filters)
          .filter(([_, value]) => value !== undefined)
          .reduce((acc, [key, value]) => ({ ...acc, [key]: String(value) }), {} as Record<string, string>)
      ).toString() : '';
      
      const url = queryParams ? `${this.basePath}/inbox?${queryParams}` : `${this.basePath}/inbox`;
      const rawResponse = await this.http.get<BackendInboxListResponse>(url, { 
        headers: { 'Content-Type': 'application/json' }
      });
      
      // Validate response format
      const validatedResponse = this.validateResponse<BackendInboxListResponse>(
        rawResponse,
        BackendInboxListResponseSchema,
        'getInboxItems'
      );
      
      // Handle error response
      if (!isBackendSuccessResponse(validatedResponse)) {
        throw BackendApiError.fromBackendError(validatedResponse);
      }
      
      const { data } = validatedResponse;
      
      // Transform to standardized format with proper typing including required fields
      return {
        items: data.items.map(item => ({
          id: item.id,
          originalContent: item.content || item.text || item.title || '',
          capturedAt: item.capturedAt,
          capturedByPersonId: item.capturedBy || 'unknown',
          processingStatus: item.status === 'processed' ? 'processed' as const : 'unprocessed' as const,
          clarification: item.clarification || undefined,
          isActionable: item.isActionable || undefined,
          // These fields are not available in backend response yet - use undefined for now
          lastRefinedAt: undefined,
          refinedByPersonId: undefined,
        })),
        pagination: {
          totalCount: data.total,
          // Add more pagination info if available
        },
        totalCount: data.total,
      };
    });
  }

  async getInboxItem(id: string): Promise<GTDInboxItemDTO | null> {
    const response = await this.http.get<any>(`${this.basePath}/inbox/${id}`);
    return response.item || null;
  }

  async captureInboxItem(request: CaptureInboxItemRequest): Promise<GTDInboxItemDTO> {
    const response = await this.http.post<any>(`${this.basePath}/inbox`, request);
    return response.item || response;
  }

  async refineInboxItem(id: string, request: RefineInboxItemRequest): Promise<GTDInboxItemDTO> {
    const response = await this.http.post<any>(`${this.basePath}/inbox/${id}/refine`, request);
    return response.item || response;
  }

  async clarifyInboxItem(id: string, request: ClarifyInboxItemRequest): Promise<GTDInboxItemDTO> {
    const response = await this.http.post<any>(`${this.basePath}/inbox/${id}/clarify`, request);
    return response.item || response;
  }

  async processInboxItem(id: string, request: ProcessInboxItemRequest): Promise<ProcessInboxResponse> {
    return this.http.post(`${this.basePath}/inbox/${id}/process`, request);
  }

  async deleteInboxItem(id: string): Promise<void> {
    await this.http.delete(`${this.basePath}/inbox/${id}`);
  }

  // Project Operations
  async getProjects(
    filters?: ProjectQueryFilters & ListQueryParams
  ): Promise<ListResponse<GTDProjectDTO>> {
    const queryParams = filters ? new URLSearchParams(
      Object.entries(filters)
        .filter(([_, value]) => value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: String(value) }), {} as Record<string, string>)
    ).toString() : '';
    
    const url = queryParams ? `${this.basePath}/projects?${queryParams}` : `${this.basePath}/projects`;
    const response = await this.http.get<any>(url);
    
    // Transform backend response to standardized format
    if (response.items && response.total !== undefined) {
      return {
        items: response.items,
        pagination: {
          totalCount: response.total,
          page: response.meta?.page,
          pageSize: response.meta?.limit,
        },
        totalCount: response.total
      };
    }
    
    // Handle array response
    const items = Array.isArray(response) ? response : [];
    return {
      items,
      pagination: { totalCount: items.length },
      totalCount: items.length
    };
  }

  async getProject(id: string): Promise<GTDProjectDTO | null> {
    const response = await this.http.get<any>(`${this.basePath}/projects/${id}`);
    return response.item || null;
  }

  async createProject(request: CreateProjectRequest): Promise<GTDProjectDTO> {
    const response = await this.http.post<any>(`${this.basePath}/projects`, request);
    return response.item || response;
  }

  async completeProject(id: string, completedBy?: string, completionNotes?: string): Promise<GTDProjectDTO> {
    const response = await this.http.post<any>(`${this.basePath}/projects/${id}/complete`, {
      completedBy,
      completionNotes
    });
    return response.item || response;
  }

  async deferProject(id: string): Promise<GTDProjectDTO> {
    const response = await this.http.post<any>(`${this.basePath}/projects/${id}/defer`);
    return response.item || response;
  }

  async activateProject(id: string): Promise<GTDProjectDTO> {
    const response = await this.http.post<any>(`${this.basePath}/projects/${id}/activate`);
    return response.item || response;
  }

  async cancelProject(id: string): Promise<GTDProjectDTO> {
    const response = await this.http.post<any>(`${this.basePath}/projects/${id}/cancel`);
    return response.item || response;
  }

  async deleteProject(id: string): Promise<void> {
    await this.http.delete(`${this.basePath}/projects/${id}`);
  }

  // Next Action Operations
  async getNextActions(
    filters?: NextActionQueryFilters & ListQueryParams
  ): Promise<ListResponse<GTDNextActionDTO>> {
    const queryParams = filters ? new URLSearchParams(
      Object.entries(filters)
        .filter(([_, value]) => value !== undefined)
        .reduce((acc, [key, value]) => ({ ...acc, [key]: String(value) }), {} as Record<string, string>)
    ).toString() : '';
    
    const url = queryParams ? `${this.basePath}/actions?${queryParams}` : `${this.basePath}/actions`;
    const response = await this.http.get<any>(url);
    
    // Transform backend response to standardized format
    if (response.items && response.total !== undefined) {
      return {
        items: response.items,
        pagination: {
          totalCount: response.total,
          page: response.meta?.page,
          pageSize: response.meta?.limit,
        },
        totalCount: response.total
      };
    }
    
    // Handle array response
    const items = Array.isArray(response) ? response : [];
    return {
      items,
      pagination: { totalCount: items.length },
      totalCount: items.length
    };
  }

  async getNextAction(id: string): Promise<GTDNextActionDTO | null> {
    const response = await this.http.get<any>(`${this.basePath}/actions/${id}`);
    return response.item || null;
  }

  async createNextAction(request: CreateNextActionRequest): Promise<GTDNextActionDTO> {
    const response = await this.http.post<any>(`${this.basePath}/actions`, request);
    return response.item || response;
  }

  async assignNextAction(id: string, request: AssignNextActionRequest): Promise<GTDNextActionDTO> {
    const response = await this.http.post<any>(`${this.basePath}/actions/${id}/assign`, request);
    return response.item || response;
  }

  async unassignNextAction(id: string): Promise<GTDNextActionDTO> {
    const response = await this.http.post<any>(`${this.basePath}/actions/${id}/unassign`);
    return response.item || response;
  }

  async completeNextAction(id: string): Promise<GTDNextActionDTO> {
    const response = await this.http.post<any>(`${this.basePath}/actions/${id}/complete`);
    return response.item || response;
  }

  async deleteNextAction(id: string): Promise<void> {
    await this.http.delete(`${this.basePath}/actions/${id}`);
  }

  // Convenience methods that match the existing frontend API
  async getActionsByProject(projectId: string): Promise<ListResponse<GTDNextActionDTO>> {
    return this.getNextActions({ projectId });
  }

  async getActionsByContext(context: string): Promise<ListResponse<GTDNextActionDTO>> {
    return this.getNextActions({ context });
  }

  async getContexts(): Promise<ListResponse<string>> {
    const response = await this.http.get<string[]>(`${this.basePath}/contexts`);
    const contexts = Array.isArray(response) ? response : [];
    return {
      items: contexts,
      pagination: { totalCount: contexts.length },
      totalCount: contexts.length
    };
  }

  // Weekly Review
  async getWeeklyReview(reviewedBy: string): Promise<WeeklyReviewResponse> {
    const queryParams = new URLSearchParams({ reviewedBy }).toString();
    const response = await this.http.get<any>(`${this.basePath}/weekly-review?${queryParams}`, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.review || response;
  }
}